import type { GuidedSystemBuilderSchema } from '@ecomm/shared-components/v2/lib/GuidedSystemBuilder/schema'
import classNames from 'classnames'
import { useState } from 'react'

import { AIChatNavButton } from '@ecomm/ai-chat/AIChatNavButton'
import { useHideUntilMounted } from '@ecomm/shared-hooks'
import { AIChatNavExperimentWrapper } from './AIChatNavExperimentWrapper'
import { CartLink } from './CartLink'
import { NavLink } from './NavLink'
import { NavMenu } from './NavMenu'
import { type CourtesyNavItemType, cartItem } from './data'
import type { HeaderRedesignSchema } from './schema'

type CourtesyNavProps = HeaderRedesignSchema & {
  readonly items: readonly CourtesyNavItemType[]
  readonly cartQuantity: number
  readonly quoteWizard: GuidedSystemBuilderSchema
}

/**
 * TODO: remove this after `All | US | Nav | Monitoring and Phone` is done.
 *
 * Account and cart icons are not part of this experiment so we don't want to hide them.
 */
function shouldAddClasses(name: string, classes: string): string {
  return ['Account', 'Cart'].includes(name) ? '' : classes
}

function renderLink(
  item: CourtesyNavItemType,
  quoteWizard: GuidedSystemBuilderSchema
) {
  return item.openAIChat ? (
    // We don't need a wrapper here because `item` is defined in `getCourtesyNavItems` which uses `useIsAIChatExperiment` to conditionally return item data
    // Only the AI Chat courtesy nav item has a prop set for `openAIChat`
    <AIChatNavButton
      classes="place-items-center grid grid-cols-1 gap-0 p-0 text-xxs hover:border-b-primary-100 min-[1126px]:text-[13px]"
      key={item.text}
    />
  ) : (
    <NavLink
      icon={item.icon}
      mobileOnly={item.mobileOnly}
      openQuiz={item.openQuiz}
      quoteWizard={quoteWizard}
      text={item.text}
      url={item.url}
    />
  )
}

export function CourtesyNav({
  items,
  images,
  cartQuantity,
  quoteWizard
}: CourtesyNavProps) {
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(-1)

  /**
   * TODO: remove this after `All | US | Nav | Monitoring and Phone` is done.
   */
  const classes = useHideUntilMounted('CourtesyNav')

  return (
    <div className="flex h-full items-center gap-6 text-white min-[1126px]:ml-auto min-[1126px]:gap-9">
      {items.map((item, i) => {
        return item.hasDropdown ? (
          <div
            className={`hidden min-[1126px]:block h-full ${shouldAddClasses(item.text, classes)}`}
            key={`${item.text}-1`}
          >
            <NavMenu
              displayInSideNav={item.displayInSideNav}
              dropdownMenu={item.dropdownMenu}
              icon={item.icon}
              images={images}
              onToggleDropdown={(close?: boolean) => {
                setActiveDropdownIndex(
                  close ? -1 : activeDropdownIndex === i ? -1 : i
                )
              }}
              quoteWizard={quoteWizard}
              showDropdown={activeDropdownIndex === i}
              showOverlay={false}
              text={item.text}
            />
          </div>
        ) : (
          <div
            className={classNames(shouldAddClasses(item.text, classes), {
              '[&:not(:last-of-type)]:mr-2':
                items.filter(item => !item.hasDropdown).length > 1,
              'min-[1126px]:hidden': item.mobileOnly,
              'hidden min-[1126px]:grid':
                !item.mobileOnly && item.displayInSideNav
            })}
            key={`${item.text}-2`}
          >
            {item.url === cartItem.url ? (
              <CartLink
                cartQuantity={cartQuantity}
                icon={item.icon}
                text={item.text}
                url={item.url}
              />
            ) : (
              renderLink(item, quoteWizard)
            )}
          </div>
        )
      })}
      <AIChatNavExperimentWrapper />
    </div>
  )
}
